import getOr from 'lodash/fp/getOr'
import { checkStore, checkKey, checkSagaDescriptor } from './checkers'
import { DAEMON, ONCE_TILL_UNMOUNT } from './constants'

/**
 * get descriptor mode or set DAEMON by default
 *
 * @param {Object} checkSagaDescriptor
 * @return {String} saga mode
 */
const getMode = getOr(DAEMON, 'mode')

/**
 * create injectSaga function to the provided store
 *
 * @param {Object} store - redux store
 * @param {Boolean} isValid - is valid store to dont check again
 * @return {Function} injectSaga function
 */
export function injectSagaFactory(store, isValid) {
  /**
   * inject saga in provided store
   *
   * @param {String} key - store.injectedSaga key to store
   * @param {Object} descriptor - saga descriptor
   * @param {Function} descriptor.saga - generator saga function
   * @param {String} descriptor.mode - saga inject mode
   * @param {Object} args - extra args to pass to runSaga
   * @throws {InvariantError} if store, key or descriptor are invalid
   */
  return (key, descriptor, args) => {
    if (!isValid) checkStore(store)

    const newDescriptor = {
      ...descriptor,
      mode: getMode(descriptor)
    }

    checkKey(key)
    checkSagaDescriptor(newDescriptor)

    const { saga, mode } = newDescriptor
    let hasSaga = Reflect.has(store.injectedSagas, key)

    if (process.env.NODE_ENV !== 'production') {
      const oldDescriptor = store.injectedSagas[key]
      // hot reloading logic in development mode
      if (hasSaga && oldDescriptor.saga !== saga) {
        oldDescriptor.task.cancel()
        hasSaga = false
      }
    }

    if (
      !hasSaga ||
      (hasSaga && mode !== DAEMON && mode !== ONCE_TILL_UNMOUNT)
    ) {
      store.injectedSagas[key] = {
        ...newDescriptor,
        task: store.runSaga(saga, args)
      }
    }
  }
}

/**
 * create ejectSaga function for the provided store
 *
 * @param {Object} store - redux store
 * @param {Boolean} isValid - is valid store to dont check again
 * @return {Function} to eject saga from provided store
 */
export function ejectSagaFactory(store, isValid) {
  /**
   * eject the injected saga key for the provided store if is not DAEMON saga
   *
   * @param {String} key - store.injectedSaga key to eject
   * @throws {InvariantError} if store or key are invalid
   */
  return key => {
    if (!isValid) checkStore(store)
    checkKey(key)

    if (Reflect.has(store.injectedSagas, key)) {
      const descriptor = store.injectedSagas[key]
      if (descriptor.mode && descriptor.mode !== DAEMON) {
        descriptor.task.cancel()

        if (process.env.NODE_ENV === 'production') {
          // Need maintain injectedSaga key to be able to detect `ONCE_TILL_UNMOUNT`
          store.injectedSagas[key] = 'done'
        }
      }
    }
  }
}

/**
 * create inject and eject saga methods for the given store
 *
 * @param {Object} store - redux store
 * @return {{ injectSaga: Function, ejectSaga: Function }} saga injector and ejector
 */
export default function getInjectors(store) {
  checkStore(store)

  return {
    injectSaga: injectSagaFactory(store, true),
    ejectSaga: ejectSagaFactory(store, true)
  }
}
